import { Box, TableCell } from "@mui/material";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { formatWithCommasWithDecimal, handleAddToCart } from "@/utils/helpers";
import { HomeProduct } from "@/types/home";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Info } from "lucide-react";
import { trackEvent } from "@/lib/gtag";
import { LoaderState } from "@/types/cart";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";

interface PriceCellProps {
  data: HomeProduct;
  redixData: { NDP_price: string | number; qty: number };
  selected: "ndp" | "sales";
  setSelected: (type: "ndp" | "sales") => void;
}

const PriceCell = ({
  data,
  redixData,
  selected,
  setSelected,
}: PriceCellProps) => {
  const ndp = Number(redixData?.NDP_price);
  const sales = Number(data?.Sales_Price);
  const salesQty = Number(data?.Bundle);
  const availableQty = redixData?.qty ?? 0;

  const bothPricesExist =
    !isNaN(ndp) &&
    !isNaN(sales) &&
    salesQty > 0 &&
    sales > 0 &&
    ndp > 0 &&
    ndp !== sales;

  const dispatch = useAppDispatch();
  const { phoneNumber } = useAppSelector((state) => state.auth);
  const [open, setOpen] = useState<LoaderState | null>(null);


  useEffect(() => {
    if (!bothPricesExist) {
      setSelected("sales");
    }
  }, [bothPricesExist, setSelected]);

  const handleOpen = (sku: string, type: string) =>
    setOpen({ sku: sku, type: type });
  const handleClose = () => setOpen(null);

  const handleSelect = (type: "ndp" | "sales") => {
    if (type === "sales") {
      manageCartQty("inc");
    } else {
      manageCartQty("dec");
    }
    setSelected(type);
  };

  const manageCartQty = (text: string) => {
    handleAddCarts(text);
  };

  const handleAddCarts = (type: string) => {
    const bundleQty = type === "inc" ? parseInt(data?.Bundle) || 1 : 1;

    if (availableQty <= 0 && type === "inc") {
      return;
    }

    if (type === "inc") {
      if (availableQty < bundleQty) {
        return;
      }

      trackEvent({
        action: "Increase Quantity",
        category: "Product",
        label: data?.SKU_ID,
      });

      handleOpen(data?.SKU_ID, type);

      handleAddToCart(
        dispatch,
        data?.SKU_ID,
        phoneNumber,
        bundleQty,
        selected,
        handleClose
      );
    }

    if (type === "dec") {
      if (availableQty <= 0) {
        return;
      }

      trackEvent({
        action: "Decrease Quantity",
        category: "Product",
        label: data?.SKU_ID,
      });

      handleOpen(data?.SKU_ID, type);

      handleAddToCart(
        dispatch,
        data?.SKU_ID,
        phoneNumber,
        bundleQty,
        selected,
        handleClose
      );
    }
  };

  return (
    <TableCell
      align="center"
      className={`relative border border-gray-300 !font-[Raleway] p-0 !w-[200px] ${open} && ""`}
    >
      <div className="flex w-full h-full relative overflow-hidden">
        {/* Background animation layer */}
        {bothPricesExist && (
          <div
            className={clsx(
              "absolute inset-0 z-0 transition-transform duration-300 ease-in-out rounded-md",
              selected === "ndp"
                ? "bg-gradient-to-r from-blue-100 to-transparent"
                : "bg-gradient-to-l from-green-100 to-transparent"
            )}
          />
        )}

        {/* Single Price Full Width */}
        {/* Dual-style layout even if only one price exists */}
        {!bothPricesExist && ndp && (
          <TooltipProvider>
            <>
              {/* NDP tab (disabled) */}
              <div
                className={clsx(
                  "flex-1 p-2 z-1 transition-all duration-200 rounded-l-md border relative",
                  "cursor-not-allowed border-gray-300 bg-gray-100 opacity-50"
                )}
              >
                <div className="flex items-center justify-center gap-1">
                  <div className="text-[17px] font-bold text-gray-700">
                    ₹{formatWithCommasWithDecimal(ndp)}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="w-3 h-3 text-gray-600 cursor-help"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs max-w-[200px] bg-black text-white"
                    >
                      This item is available in carton pack only.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-[11px] tracking-wide text-gray-500 text-center">
                  (No MOQ)
                </div>
              </div>

              {/* Sales tab (selected by default) */}
              <div
                className={clsx(
                  "flex-1 p-2 z-1 cursor-pointer transition-all duration-200 rounded-r-md border relative",
                  selected === "sales"
                    ? "border-green-500 shadow-md bg-green-50 opacity-100"
                    : "border-gray-300 opacity-50 hover:opacity-100 hover:bg-green-50"
                )}
                onClick={() => handleSelect("sales")}
              >
                <div className="flex items-center justify-center gap-1">
                  <div className="text-[17px] font-bold text-green-800">
                    ₹{formatWithCommasWithDecimal(ndp)}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="w-3 h-3 text-green cursor-help"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs max-w-[200px] bg-green text-white-1"
                    >
                      This item is available in carton packing only.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-[11px] tracking-wide text-green-600 text-center">
                  (MOQ - {salesQty || 1} units)
                </div>
              </div>
            </>
          </TooltipProvider>
        )}

        {/* Two Prices (NDP + Sales) */}
        <TooltipProvider>
          {bothPricesExist && (
            <>
              {/* NDP */}
              <div
                className={clsx(
                  "flex-1 p-2 z-1 cursor-pointer transition-all duration-200 rounded-l-md border relative",
                  selected === "ndp"
                    ? "border-blue-500 shadow-md bg-blue-50 opacity-100"
                    : "border-gray-300 opacity-50 hover:opacity-100 hover:bg-blue-50"
                )}
                onClick={() => handleSelect("ndp")}
              >
                <div className="flex items-center justify-center gap-1">
                  <div className="text-[17px] font-bold text-blue-800">
                    ₹{formatWithCommasWithDecimal(ndp)}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="w-3 h-3 text-blue cursor-help"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs max-w-[200px] bg-blue text-white-1"
                    >
                      You can buy any required quantity. No MOQ is required.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-[11px] tracking-wide text-gray-500 text-center">
                  (No MOQ)
                </div>
              </div>

              {/* Sales */}
              <div
                className={clsx(
                  "flex-1 p-2 z-1 cursor-pointer transition-all duration-200 rounded-r-md border relative",
                  selected === "sales"
                    ? "border-green-500 shadow-md bg-green-50 opacity-100"
                    : "border-gray-300 opacity-50 hover:opacity-100 hover:bg-green-50"
                )}
                onClick={() => handleSelect("sales")}
              >
                <div className="flex items-center justify-center gap-1">
                  <div className="text-[17px] font-bold text-green-800">
                    ₹{formatWithCommasWithDecimal(sales)}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="w-3 h-3 text-green cursor-help"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="text-xs max-w-[200px] bg-green text-white-1"
                    >
                      This price applies for standard carton size. MOQ is
                      required.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="text-[11px] tracking-wide text-green-600 text-center whitespace-nowrap">
                  (MOQ - {salesQty} units)
                </div>
              </div>
            </>
          )}
        </TooltipProvider>
      </div>

      {/* Est. Margin - show only if no Sales_Price */}
      {/* {(!data?.Sales_Price || Number(data?.Sales_Price) === 0) &&
        calculateUpdatedDiscountPercentage(
          Number(data?.MRP_price),
          ndp,
          data?.GST
        ) > 2 && (
          <Typography
            position="absolute"
            left="50%"
            sx={{ transform: "translateX(-50%)", top: "70%" }}
            fontSize="10px"
            bgcolor="var(--color-peach)"
            whiteSpace="nowrap"
            borderRadius="10px"
            paddingX="5px"
            color="white"
            className="!font-[Raleway]"
          >
            Est. margin:{" "}
            <b>
              <AnimatedQuantity
                value={calculateUpdatedDiscountPercentage(
                  Number(data?.MRP_price),
                  ndp,
                  data?.GST
                )}
              />
              %
            </b>
          </Typography>
        )} */}

      {/* Note */}
      <Box
        sx={{
          whiteSpace: "nowrap",
          fontSize: "10px",
          color: "gray",
          position: "absolute",
          bottom: "5%",
          left: "50%",
          transform: "translateX(-50%)",
          fontWeight: 500,
        }}
      >
        <strong>Note:</strong> Dealer Price per 1 unit
      </Box>
    </TableCell>
  );
};

export default React.memo(PriceCell);
